/**
 * Portal Layout - Member Dashboard Layout
 */
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Calendar, 
  Dumbbell, 
  Apple, 
  CreditCard,
  Settings,
  HelpCircle,
  LogOut,
  Menu,
  X,
  User,
  ChevronDown,
  Gauge,
  Briefcase
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

// OFW Logo Component
function OFWLogo() {
  return (
    <div className="flex items-center gap-3">
      <div className="bg-black px-3 py-1.5 flex items-center">
        <span className="text-xl font-heading font-bold tracking-tight">
          <span className="text-white">O</span>
          <span className="text-white">F</span>
          <span className="text-[#DB2777]">W</span>
        </span>
      </div>
      <span className="hidden sm:block text-xs font-heading font-semibold tracking-widest text-neutral-800">
        OLYMPIC FIT<br/>WELLNESS
      </span>
    </div>
  );
}

export function PortalLayout({ children }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { profile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user has admin/cockpit access
  const userRole = profile?.role || 'member';
  const hasCockpitAccess = ['super_admin', 'cms_admin', 'admin', 'staff', 'trainer', 'founder', 'manager'].includes(userRole);
  const hasAdminAccess = ['super_admin', 'admin', 'founder'].includes(userRole);
  const hasCMSAccess = ['super_admin', 'admin', 'cms_admin', 'staff', 'trainer', 'founder', 'manager'].includes(userRole);
  const hasStaffAccess = ['super_admin', 'admin', 'staff', 'trainer', 'cms_admin', 'founder', 'manager'].includes(userRole);

  // Get cockpit path based on role - staff go to staff portal which has CMS access
  const getCockpitPath = () => {
    if (hasAdminAccess) return '/admin';
    if (hasStaffAccess) return '/staff';
    return '/portal';
  };

  const navItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/portal' },
    { name: 'My Schedule', icon: Calendar, path: '/portal/schedule' },
    { name: 'Book Classes', icon: Dumbbell, path: '/portal/classes' },
    { name: 'My Workouts', icon: Dumbbell, path: '/portal/workouts' },
    { name: 'Nutrition', icon: Apple, path: '/portal/nutrition' },
    { name: 'Membership', icon: CreditCard, path: '/portal/membership' },
    { name: 'Settings', icon: Settings, path: '/portal/settings' },
    { name: 'Support', icon: HelpCircle, path: '/portal/support' },
  ];

  // Add Cockpit item if user has access
  if (hasCockpitAccess) {
    navItems.push({
      name: 'Cockpit',
      icon: Gauge,
      path: getCockpitPath(),
      highlight: true,
      badge: userRole.replace('_', ' ').toUpperCase()
    });
  }

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Top Navigation */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-neutral-200 z-40">
        <div className="flex items-center justify-between h-full px-4 lg:px-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
            >
              {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <Link to="/">
              <OFWLogo />
            </Link>
          </div>

          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-3 p-2 hover:bg-neutral-100 rounded-lg"
            >
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {profile?.full_name?.[0] || 'U'}
                  </span>
                </div>
              )}
              <span className="hidden sm:block text-sm font-medium text-neutral-700">
                {profile?.full_name || 'User'}
              </span>
              <ChevronDown className="w-4 h-4 text-neutral-400" />
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-neutral-200 rounded-lg py-2 shadow-lg">
                {hasCockpitAccess && (
                  <>
                    <Link
                      to={getCockpitPath()}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-purple-700 hover:bg-purple-50 font-medium"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Gauge className="w-4 h-4" />
                      Open Cockpit
                    </Link>
                    <hr className="my-2 border-neutral-100" />
                  </>
                )}
                <Link
                  to="/portal/settings"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
                <Link
                  to="/"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  <User className="w-4 h-4" />
                  Back to Site
                </Link>
                <hr className="my-2 border-neutral-100" />
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    handleLogout();
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`fixed top-16 left-0 bottom-0 w-64 bg-white border-r border-neutral-200 z-30 transition-transform lg:translate-x-0 ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || 
                           (item.path.startsWith('/admin') && location.pathname.startsWith('/admin')) ||
                           (item.path.startsWith('/staff') && location.pathname.startsWith('/staff'));
            const isCockpit = item.highlight;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isCockpit
                    ? isActive
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md'
                      : 'bg-gradient-to-r from-purple-500/10 to-indigo-500/10 text-purple-700 hover:from-purple-500/20 hover:to-indigo-500/20 border border-purple-200'
                    : isActive 
                      ? 'bg-[#3B82F6]/10 text-[#3B82F6]' 
                      : 'text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium flex-1">{item.name}</span>
                {item.badge && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                    isCockpit && isActive 
                      ? 'bg-white/20 text-white' 
                      : 'bg-purple-100 text-purple-700'
                  }`}>
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Quick Stats */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-neutral-200 bg-neutral-50">
          <div className="text-center">
            <p className="text-xs text-neutral-500 mb-1">Current Plan</p>
            <p className="font-heading font-bold text-[#3B82F6]">
              {profile?.membership_tier?.toUpperCase() || 'FREE'}
            </p>
            {hasCockpitAccess && (
              <p className="text-[10px] text-purple-600 mt-1 font-medium">
                {userRole.replace('_', ' ').toUpperCase()} ACCESS
              </p>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 min-h-screen">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}

export default PortalLayout;
